import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Bell, User, Menu, LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import api, { getApiBaseUrl } from '@/lib/api';
import { authStore } from '@/lib/authStore';
import { clearUserThemeStorage } from '@/hooks/useUserTheme';
import { resolveUploadUrl } from '@/lib/assets/resolveUploadUrl';
import { emitLogout } from '@/lib/auth/sessionCoordinator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AdminHeaderProps {
  onMenuClick: () => void;
}

export const AdminHeader = ({ onMenuClick }: AdminHeaderProps) => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [primaryRoleLabel, setPrimaryRoleLabel] = useState('Người dùng');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadUserData = () => {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          setUserName(user.name || '');
          const role = user.role?.trim();
          setPrimaryRoleLabel(
            !role ? 'Người dùng' : role.toLowerCase() === 'admin' ? 'Quản trị viên' : role
          );
          if (user.avatar_url) {
            setAvatarUrl(resolveUploadUrl(user.avatar_url));
          }
        } catch {}
      }
    };

    loadUserData();

    window.addEventListener('storage', loadUserData);
    return () => window.removeEventListener('storage', loadUserData);
  }, []);

  const handleLogout = async () => {
    const userId = authStore.getState().user?.id;
    if (userId) clearUserThemeStorage(userId);
    try {
      await api.post('/auth/logout', {});
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      emitLogout('AdminHeader:handleLogout');
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-jira-surface border-b border-jira-border px-4 lg:px-6 py-3">
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="lg:hidden rounded-md h-9 w-9 hover:bg-muted"
        >
          <Menu className="w-5 h-5" />
        </Button>

        <div className="flex items-center gap-1 lg:gap-2 ml-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-md h-9 w-9 lg:h-10 lg:w-10 hover:bg-muted"
          >
            {theme === 'light' ? (
              <Moon className="w-4 h-4 lg:w-5 lg:h-5" />
            ) : (
              <Sun className="w-4 h-4 lg:w-5 lg:h-5" />
            )}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="rounded-md h-9 w-9 lg:h-10 lg:w-10 relative hover:bg-muted"
          >
            <Bell className="w-4 h-4 lg:w-5 lg:h-5" />
            <span className="absolute top-2 right-2 w-2 h-2 bg-destructive rounded-full border-2 border-jira-surface" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="gap-2 lg:gap-3 rounded-md h-9 lg:h-10 px-2 lg:pl-2 lg:pr-3 hover:bg-muted cursor-pointer"
              >
                <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-md overflow-hidden border border-jira-border shrink-0">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-primary flex items-center justify-center">
                      <User className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-primary-foreground" />
                    </div>
                  )}
                </div>
                <div className="hidden lg:flex flex-col justify-center leading-tight text-left">
                  <p className="text-sm font-medium text-jira-text">{userName}</p>
                  <span className="flex items-center gap-1 text-xs text-jira-textSubtle">
                    <span>{primaryRoleLabel}</span>
                    <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                  </span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-md p-1 bg-popover">
              <DropdownMenuLabel>Tài khoản của tôi</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="rounded-md cursor-pointer"
                onClick={() => navigate('/profile')}
              >
                <User className="w-4 h-4 mr-2" />
                Hồ sơ
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="rounded-md cursor-pointer text-destructive focus:text-destructive"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Đăng xuất
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
